import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, LineChart, Line, Cell, ReferenceLine, Legend,
} from 'recharts';
import {
  FlaskConical, TrendingDown, TrendingUp, Search, BarChart3,
  DollarSign, Activity, Target, Cpu, Clock, Zap, ArrowDownRight,
  ArrowUpRight, ChevronRight, Loader2, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

const fmt = (n) => {
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return n < 0.01 ? `$${n.toFixed(4)}` : `$${n.toFixed(2)}`;
};

const RDCostAnalysis = () => {
  const [knownMPNs, setKnownMPNs] = useState([]);
  const [selectedMPN, setSelectedMPN] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    api.get('/cost-savings/known-mpns')
      .then(res => setKnownMPNs(res.data))
      .catch(() => toast.error('Failed to load components'));
  }, []);

  const categories = useMemo(() => {
    const cats = new Set(knownMPNs.map(m => m.category));
    return ['all', ...Array.from(cats).sort()];
  }, [knownMPNs]);

  const filteredParts = useMemo(() => {
    return knownMPNs.filter(m => {
      const matchSearch = !searchQuery || m.mpn.toLowerCase().includes(searchQuery.toLowerCase()) || m.manufacturer.toLowerCase().includes(searchQuery.toLowerCase());
      const matchCat = categoryFilter === 'all' || m.category === categoryFilter;
      return matchSearch && matchCat;
    });
  }, [knownMPNs, searchQuery, categoryFilter]);

  const handleAnalyze = async (mpn, spotPrice) => {
    setSelectedMPN(mpn);
    setLoading(true);
    try {
      const { data } = await api.post('/cost-savings/analyze-part', {
        mpn,
        current_price: spotPrice,
        quantity: 1000,
        distributor_type: 'both',
      });
      setAnalysis(data);
    } catch {
      toast.error('Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="rd-cost-analysis">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2" data-testid="rd-page-title">
            <FlaskConical className="w-5 h-5 text-indigo-600" />
            R&D Cost Analysis
          </h1>
          <p className="text-sm text-gray-500 mt-1">Price range intelligence and cost optimization for R&D teams</p>
        </div>
        <Badge className="bg-indigo-50 text-indigo-700 border-0 text-xs">
          {knownMPNs.length} Components Available
        </Badge>
      </div>

      {/* Filters */}
      <Card className="border-gray-200 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by MPN or manufacturer..."
                className="pl-9"
                data-testid="rd-search"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-48" data-testid="rd-category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(c => (
                  <SelectItem key={c} value={c}>{c === 'all' ? 'All Categories' : c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Components List */}
        <Card className="border-gray-200 shadow-sm lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-900">Components Library</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[600px] overflow-y-auto">
              {filteredParts.map((part) => (
                <button
                  key={part.mpn}
                  onClick={() => handleAnalyze(part.mpn, part.spotPrice)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-indigo-50/50 transition-colors ${
                    selectedMPN === part.mpn ? 'bg-indigo-50 border-l-2 border-l-indigo-500' : ''
                  }`}
                  data-testid={`rd-part-${part.mpn}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs font-mono font-semibold text-gray-900">{part.mpn}</div>
                      <div className="text-[10px] text-gray-500 mt-0.5">{part.manufacturer}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-semibold text-gray-900">{fmt(part.spotPrice)}</div>
                      <Badge className="bg-gray-100 text-gray-500 border-0 text-[9px] mt-0.5">{part.category}</Badge>
                    </div>
                  </div>
                </button>
              ))}
              {filteredParts.length === 0 && (
                <div className="p-6 text-center text-sm text-gray-400">No components match your filter</div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Analysis Panel */}
        <div className="lg:col-span-2 space-y-6">
          {loading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-500 mr-2" />
              <span className="text-sm text-gray-500">Analyzing {selectedMPN}...</span>
            </div>
          )}

          {!loading && !analysis && (
            <Card className="border-gray-200 shadow-sm">
              <CardContent className="p-12 text-center">
                <FlaskConical className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-base font-semibold text-gray-700 mb-2">Select a Component</h3>
                <p className="text-sm text-gray-400">Click on any component from the library to see its full price range analysis</p>
              </CardContent>
            </Card>
          )}

          {!loading && analysis && (
            <>
              {/* Component Header */}
              <Card className="border-gray-200 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h2 className="text-lg font-bold text-gray-900 font-mono">{analysis.part.mpn}</h2>
                        <Badge className={`text-[10px] border-0 ${analysis.part.lifecycle === 'Active' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {analysis.part.lifecycle}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500">{analysis.part.make} — {analysis.part.desc}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-indigo-700">{fmt(analysis.marketMedian)}</div>
                      <div className="text-[10px] text-gray-400">Market Median</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Lowest Spot', value: fmt(analysis.lowestSpot), icon: DollarSign, color: 'text-green-700' },
                  { label: 'Percentile', value: `${analysis.percentile.toFixed(0)}th`, icon: BarChart3, color: analysis.percentile < 50 ? 'text-green-700' : 'text-amber-600' },
                  { label: 'Signal', value: analysis.holdBuy, icon: analysis.holdBuy === 'BUY' ? TrendingDown : Clock, color: analysis.holdBuy === 'BUY' ? 'text-green-700' : 'text-amber-600', sub: `${analysis.confidence.toFixed(0)}% conf.` },
                  { label: 'Sources', value: analysis.sources, icon: Activity, color: 'text-gray-900' },
                ].map(kpi => (
                  <Card key={kpi.label} className="border-gray-200 shadow-sm">
                    <CardContent className="p-3">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <kpi.icon className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-[10px] text-gray-500">{kpi.label}</span>
                      </div>
                      <div className={`text-lg font-bold ${kpi.color}`}>{kpi.value}</div>
                      {kpi.sub && <div className="text-[9px] text-gray-400">{kpi.sub}</div>}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Price Range Distribution */}
              <Card className="border-gray-200 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-indigo-600" /> Price Range Distribution
                    <span className="text-[10px] font-normal text-gray-400 ml-auto">
                      Range: {fmt(analysis.priceRange.min)} – {fmt(analysis.priceRange.max)}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={analysis.priceDistribution} barCategoryGap="1%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 9 }} interval={3} />
                      <YAxis tick={{ fontSize: 9 }} />
                      <Tooltip contentStyle={{ fontSize: 11 }} />
                      <ReferenceLine
                        x={analysis.priceDistribution.find(p => p.price >= analysis.marketMedian)?.label}
                        stroke="#4f46e5"
                        strokeWidth={2}
                        strokeDasharray="4 4"
                        label={{ value: 'Median', fill: '#4f46e5', fontSize: 10 }}
                      />
                      <ReferenceLine
                        x={analysis.priceDistribution.find(p => p.price >= analysis.basePrice)?.label}
                        stroke="#dc2626"
                        strokeWidth={2}
                        strokeDasharray="4 4"
                        label={{ value: 'Spot', fill: '#dc2626', fontSize: 10 }}
                      />
                      <Bar dataKey="density" radius={[2, 2, 0, 0]}>
                        {analysis.priceDistribution.map((entry, i) => (
                          <Cell key={i} fill={entry.price <= analysis.marketMedian ? '#6366f1' : '#c7d2fe'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="flex items-center gap-4 mt-2 text-[10px] text-gray-500">
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-indigo-500" /> Below Median</span>
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-indigo-200" /> Above Median</span>
                    <span className="flex items-center gap-1"><span className="w-2.5 h-0.5 bg-red-500" /> Current Spot</span>
                  </div>
                </CardContent>
              </Card>

              {/* Prediction Chart */}
              <Card className="border-gray-200 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <TrendingDown className="w-4 h-4 text-indigo-600" /> Price Prediction
                    <Badge className={`ml-auto text-[10px] border-0 ${
                      analysis.holdBuy === 'BUY' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {analysis.holdBuy} — {analysis.confidence.toFixed(0)}% confidence
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <ResponsiveContainer width="100%" height={240}>
                    <AreaChart data={analysis.prediction}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 9 }} />
                      <YAxis tick={{ fontSize: 9 }} tickFormatter={(v) => `$${v.toFixed(2)}`} domain={['auto', 'auto']} />
                      <Tooltip contentStyle={{ fontSize: 11 }} formatter={(v) => v ? `$${v.toFixed(4)}` : '-'} />
                      <Area dataKey="upper" stroke="none" fill="#6366f1" fillOpacity={0.08} />
                      <Area dataKey="lower" stroke="none" fill="#6366f1" fillOpacity={0.08} />
                      <Line dataKey="actual" stroke="#16a34a" strokeWidth={2} dot={{ r: 2 }} />
                      <Line dataKey="predicted" stroke="#6366f1" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 2 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                  <div className="flex items-center gap-4 mt-2 text-[10px] text-gray-500">
                    <span className="flex items-center gap-1"><span className="w-4 h-0.5 bg-green-600" /> Historical</span>
                    <span className="flex items-center gap-1"><span className="w-4 h-0.5 bg-indigo-500" style={{ borderTop: '2px dashed #6366f1', height: 0 }} /> Predicted</span>
                    <span className="flex items-center gap-1"><span className="w-4 h-2 bg-indigo-500/10 rounded" /> Confidence</span>
                  </div>
                </CardContent>
              </Card>

              {/* Alternates */}
              {analysis.alternates.length > 0 && (
                <Card className="border-gray-200 shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <Target className="w-4 h-4 text-indigo-600" /> Design Alternates — Cost Impact
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50 text-xs">
                          <TableHead className="text-xs font-semibold">Alternate MPN</TableHead>
                          <TableHead className="text-xs font-semibold">Manufacturer</TableHead>
                          <TableHead className="text-xs font-semibold text-center">Match</TableHead>
                          <TableHead className="text-xs font-semibold text-right">Median Price</TableHead>
                          <TableHead className="text-xs font-semibold text-right">Price Range</TableHead>
                          <TableHead className="text-xs font-semibold text-right">vs Original</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {analysis.alternates.map(alt => (
                          <TableRow key={alt.mpn} className="text-xs hover:bg-gray-50">
                            <TableCell className="font-mono font-semibold text-gray-900">{alt.mpn}</TableCell>
                            <TableCell className="text-gray-600">{alt.make}</TableCell>
                            <TableCell className="text-center">
                              <Badge className={`text-[10px] border-0 ${alt.match >= 90 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                {alt.match}%
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right font-semibold">{fmt(alt.medianPrice)}</TableCell>
                            <TableCell className="text-right text-gray-500">{fmt(alt.minPrice)} – {fmt(alt.maxPrice)}</TableCell>
                            <TableCell className="text-right">
                              {alt.savingsAbs > 0 ? (
                                <span className="text-green-700 font-semibold flex items-center justify-end gap-1">
                                  <ArrowDownRight className="w-3 h-3" /> {fmt(alt.savingsAbs)} ({alt.savingsPct.toFixed(1)}%)
                                </span>
                              ) : (
                                <span className="text-red-600 font-semibold flex items-center justify-end gap-1">
                                  <ArrowUpRight className="w-3 h-3" /> {fmt(Math.abs(alt.savingsAbs))}
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Savings Summary for R&D */}
              <Card className="border-indigo-200 shadow-sm bg-indigo-50/30">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-1">R&D Cost Impact Summary</h3>
                      <p className="text-xs text-gray-500">Based on 1,000-unit analysis of {analysis.part.mpn}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-indigo-700">{fmt(analysis.totalSavings)}</div>
                      <div className="text-[10px] text-gray-400">potential savings</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RDCostAnalysis;
